import React, { useCallback, useMemo, useEffect, useState, useContext } from 'react';
import { Divider, mergeClasses } from '@fluentui/react-components';
import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';
import { ConversationItem } from './conversation-item';
import { useConversationItemStyles } from './styles';
import { TimeLabel } from './time-label';
import { getTimePeriod } from './utils';
import type { Conversation, ConversationListProps } from './types';
import { useChatHistoryPerfMarkers } from './hooks/use-chat-history-perf-markers';

export type { Conversation } from './types';

export const ConversationList = ({
  conversations, onConversationClick, onDelete, onRename, onLoadMore, activeConversationId, appPerfMarker,
}: ConversationListProps) => {
  const styles = useConversationItemStyles();
  const i18n = useContext(I18nContext) as I18nModel;
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const { conversationsPerfMarker } = useChatHistoryPerfMarkers(appPerfMarker);

  const groupedConversations = useMemo(() => {
    if (!conversations?.length) {
      conversationsPerfMarker?.done();
      return [];
    }

    const groups = new Map<string, Conversation[]>();
    // Group conversations by time period
    conversations.forEach((conversation) => {
      const lastInteractedTime = conversation.LastInteractedTime ?? conversation.lastInteractedTime;

      const period = getTimePeriod(new Date(lastInteractedTime), i18n);
      if (!groups.has(period)) {
        groups.set(period, []);
      }
      groups.get(period)?.push(conversation);
    });
    conversationsPerfMarker?.done();
    return Array.from(groups.entries());
  }, [conversations, i18n, conversationsPerfMarker]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLUListElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (scrollHeight - scrollTop <= clientHeight + 50) {
      onLoadMore!();
    }
  }, [onLoadMore]);

  useEffect(() => {
    if (isFirstLoad && groupedConversations.length > 0) {
      setIsFirstLoad(false);
      const firstConversationItem = document.querySelector('.conversation-item:first-of-type');

      if (firstConversationItem) {
        (firstConversationItem as HTMLElement).focus();
      }
    }
  }, [isFirstLoad, groupedConversations]);

  return (
    <ul
      className={mergeClasses(styles.listContainer, 'conversation-list')}
      onScroll={handleScroll}
    >
      {groupedConversations.map(([period, periodConversations]) => (
        <React.Fragment key={period}>
          <TimeLabel label={period} />
          {periodConversations.map((conversation, index) => {
            const conversationId = conversation.ConversationId ?? conversation.conversationId;
            const title = conversation.Title ?? conversation.title;

            return (
              <li key={conversationId}>
                <ConversationItem
                  conversationId={conversationId}
                  title={title}
                  onClick={onConversationClick}
                  onDelete={onDelete}
                  onRename={onRename}
                  isActive={conversationId === activeConversationId}
                  position={index + 1}
                  conversationsLength={periodConversations.length}
                  timePeriod={period}
                />
              </li>
            );
          })}
          <li>
            <Divider className={styles.divider} />
          </li>
        </React.Fragment>
      ))}
    </ul>
  );
};
